import { LineHelper } from '../../../../helpers/line.helper';
import { TrafficSituationHelper } from '../../../../helpers/traffic-situation.helper';
import { ILine } from '../../../../models/line.model';
import { ITrafficSituation } from '../../../../models/traffic-situation.model';
import { TransportMode } from '../../../../models/transport-mode.enum';
import { TranslationService } from '../../../../services/translation.service';
import { Icon } from '../Icon';
import { IconColor } from '../Icon/icon-color.enum';
import { IconMotif } from '../Icon/icon-motif.enum';
import { IconVariant } from '../Icon/icon-variant.enum';
import groupBy from 'lodash/groupBy';

function AiEnhancedTrafficSituation({ trafficSituation }: { trafficSituation: ITrafficSituation }) {
  if (!trafficSituation || !trafficSituation.aiGeneratedInformation) {
    return null;
  }

  const distinctLines = TrafficSituationHelper.getDistinctLines(trafficSituation);

  return (
    <div className="ai-enhanced-traffic-situation last-child-margin-bottom-none">
      <Fact {...getSeverityIcon(trafficSituation.severity)} altText={getSeverityIconAltText(trafficSituation.severity)} title={getSeverityIconAltText(trafficSituation.severity)} className="mb-2">
        <h4>{trafficSituation.aiGeneratedInformation.summary}</h4>
      </Fact>
      <LineListFact lines={distinctLines} className="mb-2" />
      <ValidityPeriodFact trafficSituation={trafficSituation} className="mb-2" />
      {trafficSituation.aiGeneratedInformation.alternatives && (
        <Fact
          iconMotif={IconMotif.Alternatives}
          className="mb-2"
          altText={TranslationService.get('trafficsituationaienhancementevaluationpage.alternatives')}
          title={TranslationService.get('trafficsituationaienhancementevaluationpage.alternatives')}
        >
          <span>{trafficSituation.aiGeneratedInformation.alternatives}</span>
        </Fact>
      )}
      {trafficSituation.aiGeneratedInformation.reason && (
        <Fact
          iconMotif={IconMotif.Exclamation}
          iconVariant={IconVariant.Circle}
          className="mb-2"
          altText={TranslationService.get('trafficsituationaienhancementevaluationpage.reason')}
          title={TranslationService.get('trafficsituationaienhancementevaluationpage.reason')}
        >
          <span>{trafficSituation.aiGeneratedInformation.reason}</span>
        </Fact>
      )}
    </div>
  );
}

function Fact({
  children,
  iconMotif,
  iconColor,
  iconVariant,
  altText,
  title,
  className = '',
}: {
  children: React.ReactNode;
  iconMotif: IconMotif;
  iconColor?: IconColor;
  iconVariant?: IconVariant;
  altText?: string | undefined;
  title?: string | undefined;
  className?: string;
}) {
  return (
    <div className={`d-flex align-items-center ${className}`}>
      <Icon motif={iconMotif} color={iconColor} variant={iconVariant} altText={altText} title={title} className="me-2" />
      {children}
    </div>
  );
}

function LineListFact({ lines, className = '' }: { lines: ILine[] | undefined; className?: string }): React.ReactNode | null {
  if (!lines || lines.length === 0) return null;

  const linesByTransportMode = groupBy(lines, (line) => line.transportMode);

  return (
    <ul className={`list-unstyled ${className ?? ''}`}>
      {Object.entries(linesByTransportMode).map(([transportMode, transportModeLines]) => (
        <li key={transportMode}>
          {transportMode === TransportMode.train ? (
            <TrainsWithLineBadges lines={transportModeLines} />
          ) : (
            <TransportModeWithLineBadges transportMode={transportMode as TransportMode} lines={transportModeLines} />
          )}
        </li>
      ))}
    </ul>
  );
}

// TODO: Add support for showTrainDirectionName, showNonTrainDirectionName, showAppliesFromDateOnFutureLines, directionAsStrong in line badges components

function TrainsWithLineBadges({ lines }: { lines: ILine[] }): React.ReactNode {
  return (
    <Fact iconMotif={IconMotif.Train}>
      <div className="last-child-margin-bottom-none">
        {lines.map((line) => (
          <div key={line.gid} className="d-flex align-items-center mb-1">
            <LineBadge line={line} className="me-1" />
            <span>{line.directionName}</span>
          </div>
        ))}
      </div>
    </Fact>
  );
}

function TransportModeWithLineBadges({ transportMode, lines }: { transportMode: TransportMode; lines: ILine[] }): React.ReactNode {
  return (
    <Fact iconMotif={transportModeToIconMotif(transportMode)}>
      {lines.map((line) => (
        <LineBadge key={line.gid} line={line} className="me-1" />
      ))}
    </Fact>
  );
}

function LineBadge({ line, className = '' }: { line: ILine; className?: string }): React.ReactNode {
  return (
    <span
      className={`px-1 flex-shrink-0 rounded border d-flex align-items-center justify-content-center ${className ?? ''}`}
      style={{
        minHeight: '1.5rem',
        minWidth: '1.5rem',
        lineHeight: '1rem',
        backgroundColor: LineHelper.getBackgroundColor(line),
        color: LineHelper.getForegroundColor(line),
        borderColor: LineHelper.getBorderColor(line),
      }}
    >
      <span className="visually-hidden">{TranslationService.get('common.line')}</span>
      {line.name ? <span className="fw-bold small">{line.name}</span> : <span className="fw-bold small">{line.shortName}</span>}
    </span>
  );
}

function ValidityPeriodFact({ trafficSituation, className = '' }: { trafficSituation: ITrafficSituation; className?: string }): React.ReactNode | null {
  if (!trafficSituation.aiGeneratedInformation || (!trafficSituation.aiGeneratedInformation.startTime && !trafficSituation.aiGeneratedInformation.endTime)) return null;

  return (
    <Fact
      iconMotif={IconMotif.Calendar}
      className={className}
      altText={TranslationService.get('trafficsituationaienhancementevaluationpage.validityperiod')}
      title={TranslationService.get('trafficsituationaienhancementevaluationpage.validityperiod')}
    >
      <div>
        {trafficSituation.aiGeneratedInformation.startTime && (
          <>
            <span className="fw-bold">{TranslationService.get('common.from')}: </span> {trafficSituation.aiGeneratedInformation.startTime.setLocale('sv').toFormat("cccc d MMMM 'kl' HH:mm")}
          </>
        )}
        {trafficSituation.aiGeneratedInformation.startTime && trafficSituation.aiGeneratedInformation.endTime && <br />}
        {trafficSituation.aiGeneratedInformation.endTime && (
          <>
            <span className="fw-bold">{TranslationService.get('common.to')}: </span> {trafficSituation.aiGeneratedInformation.endTime.setLocale('sv').toFormat("cccc d MMMM 'kl' HH:mm")}
          </>
        )}
      </div>
    </Fact>
  );
}

function getSeverityIcon(severity: string): { iconMotif: IconMotif; iconColor: IconColor; iconVariant?: IconVariant } {
  switch (severity) {
    case 'severe':
      return { iconMotif: IconMotif.Exclamation, iconColor: IconColor.Red600, iconVariant: IconVariant.HexagonFilled };
    case 'normal':
      return { iconMotif: IconMotif.Exclamation, iconColor: IconColor.Orange500, iconVariant: IconVariant.TriangleFilled };
    default:
      return { iconMotif: IconMotif.Info, iconColor: IconColor.Blue800, iconVariant: IconVariant.CircleFilled };
  }
}

function getSeverityIconAltText(severity: string): string {
  switch (severity) {
    case 'severe':
      return TranslationService.get('trafficsituationaienhancementevaluationpage.severity.severe');
    case 'slight':
      return TranslationService.get('trafficsituationaienhancementevaluationpage.severity.slight');
    default:
      return TranslationService.get('trafficsituationaienhancementevaluationpage.severity.normal');
  }
}

function transportModeToIconMotif(transportMode: TransportMode): IconMotif {
  switch (transportMode) {
    case TransportMode.bus:
      return IconMotif.Bus;
    case TransportMode.bussondemand:
      return IconMotif.BussOnDemand;
    case TransportMode.car:
      return IconMotif.Car;
    case TransportMode.train:
      return IconMotif.Train;
    default:
      return IconMotif.Alternatives;
  }
}

export default AiEnhancedTrafficSituation;
